import React, { useEffect, useCallback } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Custom Confirm Modal - Replaces window.confirm()
 * App Store compliant modal dialog
 *
 * Usage:
 *   <ConfirmModal
 *     isOpen={showConfirm}
 *     title="Delete Item?"
 *     message="This action cannot be undone."
 *     confirmText="Delete"
 *     cancelText="Cancel"
 *     variant="danger" // 'danger' | 'warning' | 'default'
 *     onConfirm={() => { deleteItem(); setShowConfirm(false); }}
 *     onCancel={() => setShowConfirm(false)}
 *   />
 */
export default function ConfirmModal({
    isOpen,
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'default', // 'danger' | 'warning' | 'default'
    onConfirm,
    onCancel,
    loading = false
}) {
    // Handle escape key
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && !loading) {
            onCancel?.();
        }
    }, [onCancel, loading]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    const variantStyles = {
        danger: {
            icon: 'bg-red-100 text-red-600',
            button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
        },
        warning: {
            icon: 'bg-yellow-100 text-yellow-600',
            button: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
        },
        default: {
            icon: 'bg-blue-100 text-blue-600',
            button: 'bg-slate-900 hover:bg-slate-800 focus:ring-slate-500'
        }
    };

    const styles = variantStyles[variant] || variantStyles.default;

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                onClick={loading ? undefined : onCancel}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
                {/* Close button */}
                <button
                    onClick={onCancel}
                    disabled={loading}
                    className="absolute top-4 right-4 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>

                {/* Icon */}
                <div className={`w-12 h-12 rounded-full ${styles.icon} flex items-center justify-center mx-auto mb-4`}>
                    <ExclamationTriangleIcon className="w-6 h-6" />
                </div>

                {/* Title */}
                <h3
                    id="confirm-modal-title"
                    className="text-lg font-black text-slate-900 text-center mb-2"
                >
                    {title}
                </h3>

                {/* Message */}
                <p className="text-sm text-slate-600 text-center mb-6 leading-relaxed">
                    {message}
                </p>

                {/* Buttons */}
                <div className="flex space-x-3">
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className={`flex-1 py-3 px-4 ${styles.button} text-white font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center`}
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes scale-in {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                .animate-scale-in {
                    animation: scale-in 0.2s ease-out;
                }
            `}</style>
        </div>
    );
}

/**
 * Hook for easy confirm modal usage
 *
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *
 *   const handleDelete = async () => {
 *     const confirmed = await confirm({
 *       title: 'Delete Item?',
 *       message: 'This cannot be undone.',
 *       variant: 'danger'
 *     });
 *     if (confirmed) {
 *       // do delete
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <button onClick={handleDelete}>Delete</button>
 *       <ConfirmDialog />
 *     </>
 *   );
 */
export function useConfirm() {
    const [state, setState] = React.useState({
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        variant: 'default',
        resolve: null
    });

    const confirm = React.useCallback((options = {}) => {
        return new Promise((resolve) => {
            setState({
                isOpen: true,
                title: options.title || 'Confirm Action',
                message: options.message || 'Are you sure?',
                confirmText: options.confirmText || 'Confirm',
                cancelText: options.cancelText || 'Cancel',
                variant: options.variant || 'default',
                resolve
            });
        });
    }, []);

    const handleConfirm = React.useCallback(() => {
        state.resolve?.(true);
        setState(prev => ({ ...prev, isOpen: false }));
    }, [state.resolve]);

    const handleCancel = React.useCallback(() => {
        state.resolve?.(false);
        setState(prev => ({ ...prev, isOpen: false }));
    }, [state.resolve]);

    const ConfirmDialog = React.useCallback(() => (
        <ConfirmModal
            isOpen={state.isOpen}
            title={state.title}
            message={state.message}
            confirmText={state.confirmText}
            cancelText={state.cancelText}
            variant={state.variant}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
        />
    ), [state, handleConfirm, handleCancel]);

    return { confirm, ConfirmDialog };
}
